import { resolveSemanticColor } from '@/theme/semanticTokens';
import {
  BlockOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  DonutLargeIcon,
  EditOutlinedIcon,
  ErrorOutline,
  EventAvailableOutlinedIcon,
  EventBusyOutlinedIcon,
  EventOutlinedIcon,
  HelpOutline,
  PlayCircleFilledWhiteOutlinedIcon,
  ReleasesIcon,
  ScheduleOutlinedIcon,
} from '@/ui/primitives/icons';
import type { SvgIconComponent } from '@/ui/primitives';
import type { Theme } from '@mui/material/styles';

type StatusFilterOptionIcon = {
  countIcon: SvgIconComponent;
  countIconColor: (theme: Theme) => string;
};

const resolveStatusFilterIcon = (status: string): SvgIconComponent => {
  if (status === 'draft') return EditOutlinedIcon;
  if (status === 'planned') return EventOutlinedIcon;
  if (status === 'ready') return EventAvailableOutlinedIcon;
  if (status === 'overdue') return EventBusyOutlinedIcon;
  if (status === 'released') return ReleasesIcon;
  if (status === 'passed' || status === 'completed' || status === 'success') {
    return CheckCircleOutlineIcon;
  }
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'errored') return ErrorOutline;
  if (status === 'running') return PlayCircleFilledWhiteOutlinedIcon;
  if (status === 'queued') return ScheduleOutlinedIcon;
  if (status === 'skipped') return BlockOutlinedIcon;
  if (status === 'untested') return HelpOutline;
  return DonutLargeIcon;
};

const resolveStatusFilterIconColorKey = (status: string): string => {
  if (status === 'passed' || status === 'completed' || status === 'success') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'errored') return 'brown';
  if (status === 'skipped') return 'warning';
  if (status === 'running') return 'info';
  if (status === 'queued') return 'warning';
  if (status === 'released' || status === 'ready') return 'primary';
  if (status === 'overdue') return 'error';
  return 'textSecondary';
};

export const withStatusFilterOptionIcons = <TOption extends { value: string }>(
  options: readonly TOption[],
): Array<TOption & StatusFilterOptionIcon> =>
  options.map((option) => {
    const iconColorKey = resolveStatusFilterIconColorKey(option.value);
    return {
      ...option,
      countIcon: resolveStatusFilterIcon(option.value),
      countIconColor: (theme) => resolveSemanticColor(theme, iconColorKey),
    };
  });
